import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseModuleLine } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";

/**
 * Which version of each module this release ships (§5).
 *
 * <p>"PR Blocks 5.0" on its own is a label. This is what makes it a fact: Inventory 3.8,
 * Production 4.0, Accounting 2.0 → 2.1. Both versions are shown because "what did 5.0 change?"
 * is the question people actually ask, and it cannot be answered by a list of current versions
 * alone.
 *
 * <p>Applying the manifest is deliberately a separate act from editing it. Planning a version and
 * declaring it the version of record are different decisions, and a table that wrote through on
 * every keystroke would move a customer's recorded version while somebody was still thinking.
 */
export function ReleaseManifestCard({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ReleaseModuleLine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const manifest = useQuery({
    queryKey: ["release-manifest", release.id],
    queryFn: () => api.get<ReleaseModuleLine[]>(`/api/v1/releases/${release.id}/modules`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release-manifest", release.id] });
    void queryClient.invalidateQueries({ queryKey: ["project-modules", release.projectId] });
    void queryClient.invalidateQueries({ queryKey: ["modules"] });
    onChanged();
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      await api.post(`/api/v1/releases/${release.id}/modules/apply`);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not apply that.");
    } finally {
      setApplying(false);
    }
  };

  const lines = manifest.data ?? [];
  const changed = lines.filter((line) => line.changed);

  return (
    <Card>
      <CardHeader
        title="Modules in this release"
        subtitle={
          lines.length === 0
            ? "This project has no modules linked."
            : changed.length === 0
              ? "Nothing is being upgraded yet."
              : `${changed.length} of ${lines.length} ${changed.length === 1 ? "module moves" : "modules move"}.`
        }
        action={
          can("RELEASE_EDIT")
          && changed.length > 0 && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Check className="h-4 w-4" />}
              loading={applying}
              title="Make these the versions this project is recorded as running"
              onClick={() => void apply()}
            >
              Apply
            </Button>
          )
        }
      />
      <CardBody className="space-y-3">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {manifest.isPending ? (
          <LoadingState label="Loading modules…" />
        ) : lines.length === 0 ? (
          <EmptyState
            title="No modules"
            message="Link the modules this product is built from on the project's Modules tab, then create a release to plan their versions."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left">
                  <th className="pb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Module
                  </th>
                  <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Previous
                  </th>
                  <th className="pb-2 text-right text-xs font-semibold uppercase tracking-wide text-ink-500">
                    Current
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {lines.map((line) => (
                  <tr key={line.module.id}>
                    <td className="py-2.5">
                      <p className="font-medium text-ink-900">{line.module.name}</p>
                      {line.module.moduleType.sharedVersion && (
                        <p className="text-xs text-ink-500">shared across projects</p>
                      )}
                    </td>
                    <td className="tnum py-2.5 text-right font-mono text-ink-500">
                      {line.previousVersion ?? "—"}
                    </td>
                    <td
                      className={`tnum py-2.5 text-right font-mono ${
                        line.changed ? "font-semibold text-amber-700" : "text-ink-500"
                      }`}
                    >
                      {line.currentVersion ?? "—"}
                    </td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {line.changed ? (
                          <StatusBadge tone="amber">Upgraded</StatusBadge>
                        ) : (
                          <StatusBadge tone="gray">Carried</StatusBadge>
                        )}
                        {can("RELEASE_EDIT") && release.scopeFrozenAt == null && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditing(line)}
                          >
                            Set
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {changed.length > 0 && (
          <p className="text-xs text-ink-500">
            Applying records these as the versions {release.projectName} is running. A shared
            module also gets a new latest release — but other projects using it stay where they
            are until it is deployed for them.
          </p>
        )}
      </CardBody>

      <PlanVersionDrawer
        releaseId={release.id}
        line={editing}
        onClose={() => setEditing(null)}
        onSaved={refresh}
      />
    </Card>
  );
}

function PlanVersionDrawer({
  releaseId,
  line,
  onClose,
  onSaved,
}: {
  releaseId: string;
  line: ReleaseModuleLine | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [version, setVersion] = useState("");
  const [changed, setChanged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (line) {
      setVersion(line.currentVersion ?? "");
      setChanged(line.changed);
      setError(null);
    }
  }, [line]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/releases/${releaseId}/modules`, {
        moduleId: line?.module.id,
        version: version.trim() === "" ? null : version.trim(),
        changed,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that version.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={line != null}
      onClose={onClose}
      title="Version in this release"
      subtitle={line?.module.name}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="flex items-center gap-3 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5">
          <span className="tnum font-mono text-sm text-ink-500">
            {line?.previousVersion ?? "—"}
          </span>
          <ArrowRight className="h-4 w-4 text-ink-400" aria-hidden />
          <span className="tnum font-mono text-sm font-semibold text-ink-900">
            {version.trim() === "" ? "—" : version}
          </span>
        </div>

        <Field label="Version" hint="Whatever your team calls it. Not parsed or compared.">
          <Input
            autoFocus
            value={version}
            placeholder="2.1"
            onChange={(event) => setVersion(event.target.value)}
          />
        </Field>

        {/*
          A decision, not a comparison. A module rebuilt and reshipped at the same number after a
          bad build did change — somebody will have to deploy it — and inferring "unchanged" from
          two equal strings would drop that from the record.
        */}
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-300"
            checked={changed}
            onChange={(event) => setChanged(event.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium text-ink-900">
              This release upgrades it
            </span>
            <span className="block text-xs text-ink-600">
              Tick this even when the number is unchanged, if the module was rebuilt and has to be
              deployed again. Only ticked modules move when the manifest is applied.
            </span>
          </span>
        </label>
      </div>
    </Drawer>
  );
}
